import { createContext, useContext, useState, ReactNode, useMemo, useCallback, useEffect } from 'react';

interface ProductQuantityContextType {
  quantity: number | undefined;
  onChangeQuantity: (quantity: string | undefined) => void;
  maxQuantity: number;
}

const ProductQuantityContext = createContext<ProductQuantityContextType | undefined>(undefined);

interface ProductQuantityProviderProps {
  children: ReactNode;
  maxQuantity: number;
}

const getDefaultValue = (quantity: number) => Math.min(4, quantity);

export const ProductQuantityProvider = ({ children, maxQuantity }: ProductQuantityProviderProps) => {
  const [quantity, setQuantity] = useState<number | undefined>(getDefaultValue(maxQuantity));

  const onChangeQuantity = useCallback((value: string | undefined) => {
    if (value !== undefined) {
      setQuantity(Number(value));
    } else {
      setQuantity(undefined);
    }
  }, []);
  const value = useMemo(() => ({ quantity, onChangeQuantity, maxQuantity }), [maxQuantity, quantity, onChangeQuantity]);

  useEffect(() => {
    setQuantity(getDefaultValue(maxQuantity));
  }, [maxQuantity]);

  return <ProductQuantityContext.Provider value={value}>{children}</ProductQuantityContext.Provider>;
};

export const useProductQuantity = () => {
  const context = useContext(ProductQuantityContext);
  if (!context) {
    throw new Error('useProductQuantity must be used within a ProductQuantityProvider');
  }
  return context;
};
